import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FloatingInput } from '@/components/common/FloatingInput';
import {
  forgotPasswordSchema,
  resetPasswordSchema,
  ForgotPasswordFormValues,
  ResetPasswordFormValues,
} from '@/utils/validation';
import { useForgotPasswordMutation, useResetPasswordMutation } from '@/store/api/authApi';
import toast from 'react-hot-toast';

export const ForgotPasswordForm = ({
  onClose,
  onSwitch,
}: {
  onSwitch: (value: string) => void;
  onClose?: () => void;
}) => {
  const [step, setStep] = useState<'sendOtp' | 'reset'>('sendOtp');

  const [sendOtp, { isLoading: sendingOtp }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: resetting }] = useResetPasswordMutation();

  // ✅ STEP 1 FORM
  const sendOtpForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { mobile: '' },
  });

  // ✅ STEP 2 FORM
  const resetForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const mobile = sendOtpForm.watch('mobile');

  // 🔹 SEND OTP
  const handleSendOtp = async (data: ForgotPasswordFormValues) => {
    try {
      await sendOtp({ mobile: data.mobile }).unwrap();
      toast.success('OTP sent successfully');
      setStep('reset');
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to send OTP');
    }
  };

  // 🔹 RESET PASSWORD
  const handleReset = async (data: ResetPasswordFormValues) => {
    try {
      await resetPassword({
        mobile,
        code: data.otp,
        newPassword: data.newPassword,
      }).unwrap();

      toast.success('Password reset successful');
      onClose?.();
    } catch (err: any) {
      toast.error(err?.data?.message || 'Reset failed');
    }
  };

  return (
    <div className="px-4 py-10 text-white">
      <div className="flex justify-center mb-6">
        <img src="/logo.png" alt="logo" className="w-[130px]" />
      </div>

      <h2 className="text-center text-lg font-semibold mb-2">Forgot Password?</h2>

      <p className="text-center text-sm text-gray-400 mb-6">
        We’ll send OTP on your registered number
      </p>

      {/* STEP 1 */}
      {step === 'sendOtp' && (
        <form onSubmit={sendOtpForm.handleSubmit(handleSendOtp)} className="space-y-6">
          <FloatingInput
            label="Phone Number"
            register={sendOtpForm.register('mobile')}
            error={sendOtpForm.formState.errors.mobile}
          />

          <button
            type="submit"
            disabled={sendingOtp}
            className="w-full bg-primary text-black font-semibold py-2 rounded-md"
          >
            {sendingOtp ? 'Sending...' : 'SEND OTP'}
          </button>
        </form>
      )}

      {/* STEP 2 */}
      {step === 'reset' && (
        <form onSubmit={resetForm.handleSubmit(handleReset)} className="space-y-6">
          <FloatingInput
            label="Enter OTP"
            type="tel"
            register={resetForm.register('otp')}
            error={resetForm.formState.errors.otp}
          />

          <FloatingInput
            label="New Password"
            type="password"
            register={resetForm.register('newPassword')}
            error={resetForm.formState.errors.newPassword}
          />

          <FloatingInput
            label="Confirm Password"
            type="password"
            register={resetForm.register('confirmPassword')}
            error={resetForm.formState.errors.confirmPassword}
          />

          <div className="text-sm text-gray-400">
            Phone Number
            <div className="text-white">{mobile}</div>
          </div>

          <button
            type="submit"
            disabled={resetting}
            className="w-full bg-primary text-black font-semibold py-2 rounded-md"
          >
            {resetting ? 'Processing...' : 'RESET PASSWORD'}
          </button>

          <div
            onClick={() => {
              setStep('sendOtp');

              sendOtpForm.reset({
                mobile: '',
              });

              resetForm.reset({
                otp: '',
                newPassword: '',
                confirmPassword: '',
              });
            }}
            className="text-center text-xs text-gray-400 underline cursor-pointer"
          >
            Change Phone Number
          </div>
        </form>
      )}
    </div>
  );
};
